/**
 * Public page where anyone can request a 30-day trial license.
 * No admin-generated link required.
 */
import React, { useState } from 'react';
import {
  Card, Form, Input, Select, Button, Result, Row, Col, Typography, message,
} from 'antd';
import { useSearchParams } from 'react-router-dom';
import { trialLicenseApi } from './api';
import type { TrialLicenseInput } from './types';
import { DB_VENDORS } from './constants';

const { Title, Text } = Typography;

type PageState = 'form' | 'success' | 'error';

const TrialLicensePage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const machineCodeParam = searchParams.get('machine_code') || '';
  const [pageState, setPageState] = useState<PageState>('form');
  const [form] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);
  const [resultEmail, setResultEmail] = useState('');

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const values = await form.validateFields();
      const submitData: TrialLicenseInput = {
        ...values,
        supported_databases: Array.isArray(values.supported_databases)
          ? values.supported_databases.join(',')
          : values.supported_databases,
      };
      const result = await trialLicenseApi.create(submitData);
      setResultEmail(values.customer_contact_email);
      setPageState('success');
      if (!result.email_sent) {
        message.warning('License was generated but the email could not be sent. Please contact support@gudusoft.com.');
      }
    } catch (error: any) {
      message.error(error.message || 'Something went wrong');
    } finally {
      setSubmitting(false);
    }
  };

  const cardStyle: React.CSSProperties = {
    maxWidth: 720,
    margin: '40px auto',
  };

  if (pageState === 'success') {
    return (
      <Card style={cardStyle}>
        <Result
          status="success"
          title="License Sent!"
          subTitle={`Your 30-day trial license has been sent to ${resultEmail}. Please check your inbox (and spam folder).`}
        />
      </Card>
    );
  }

  return (
    <Card
      style={cardStyle}
      title={
        <div style={{ textAlign: 'center' }}>
          <Title level={3} style={{ margin: 0 }}>SQLFlow Trial License</Title>
          <Text type="secondary">Fill in the details below to receive a free 30-day trial license by email.</Text>
        </div>
      }
    >
      <Form form={form} layout="vertical" initialValues={{ machine_code: machineCodeParam }}>
        <Form.Item
          name="machine_code"
          label="Machine Code"
          rules={[{ required: true, message: 'Please enter machine code' }]}
        >
          <Input.TextArea rows={3} placeholder="Enter machine code" readOnly={!!machineCodeParam} />
        </Form.Item>

        <Form.Item
          name="supported_databases"
          label="Supported Databases (max 3)"
          rules={[
            { required: true, message: 'Please select supported databases' },
            {
              validator: (_, value) =>
                value && value.length > 3
                  ? Promise.reject('You can select up to 3 databases')
                  : Promise.resolve(),
            },
          ]}
        >
          <Select mode="multiple" placeholder="Select up to 3 databases" maxTagCount={3}>
            {DB_VENDORS.map(db => (
              <Select.Option key={db.value} value={db.value}>{db.label}</Select.Option>
            ))}
          </Select>
        </Form.Item>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="customer_company_name"
              label="Company Name"
              rules={[{ required: true, message: 'Please enter company name' }]}
            >
              <Input />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="customer_contact_name"
              label="Contact Name"
              rules={[{ required: true, message: 'Please enter contact name' }]}
            >
              <Input />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item
          name="customer_contact_email"
          label="Email Address"
          rules={[
            { required: true, message: 'Please enter your email' },
            { type: 'email', message: 'Please enter a valid email' },
          ]}
        >
          <Input placeholder="you@company.com" />
        </Form.Item>

        <Form.Item style={{ textAlign: 'center', marginBottom: 0 }}>
          <Button type="primary" size="large" loading={submitting} onClick={handleSubmit}>
            Get Trial License
          </Button>
        </Form.Item>
      </Form>
    </Card>
  );
};

export default TrialLicensePage;
